import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Chip, Tooltip, InputAdornment,
  ToggleButton, ToggleButtonGroup, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { authFetch } from '../../utils/authHelpers';
import { AI_ENGINE_URL } from '../../utils/apiConfig';

const STATUS_COLORS = {
  'In progress': '#3b82f6',
  'Offerte': '#f59e0b',
  'On Hold': '#adb3b5',
  'Afgerond': '#10b981',
  'Pre-productie': '#8b5cf6',
  'Post-productie': '#ec4899',
};

export default function ProjectBrowser({ userEmail, onPinProject, onUnpinProject, onSelectFolder, onClose }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchProjects = useCallback(async (q = '', status = null) => {
    if (!userEmail) return;
    setLoading(true);
    try {
      let url = `${AI_ENGINE_URL}/api/v1/chat/projects/browse?user_email=${encodeURIComponent(userEmail)}&limit=100`;
      if (q) url += `&q=${encodeURIComponent(q)}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      const res = await authFetch(url);
      if (res.ok) {
        setProjects(await res.json());
      } else {
        console.error('[ProjectBrowser] API error:', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      console.error('[ProjectBrowser] Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchProjects(q, statusFilter), 300));
  };

  const handleStatusFilter = (_, newStatus) => {
    setStatusFilter(newStatus);
    fetchProjects(searchQuery, newStatus);
  };

  const handlePin = async (e, project) => {
    e.stopPropagation();
    if (project.is_pinned) {
      await onUnpinProject(project.id);
    } else {
      await onPinProject(project.id);
    }
    fetchProjects(searchQuery, statusFilter);
  };

  const handleCardClick = (project) => {
    if (project.is_pinned && project.folder_id) {
      onSelectFolder(project.folder_id);
      onClose();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0, bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid rgba(45,51,53,0.08)', bgcolor: '#fff' }}>
        <FolderOpenIcon sx={{ fontSize: 24, color: '#526075', mr: 1.5 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, fontSize: 18, color: '#2d3335' }}>
          Projecten
        </Typography>
        <Tooltip title="Sluiten">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search & Filters */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f4f5', bgcolor: '#fff' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Zoek op naam, nummer of klant..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#adb3b5', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8f9fa' } }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={handleStatusFilter}
          size="small"
          sx={{ flexWrap: 'wrap', gap: 0.5 }}
        >
          <ToggleButton value={null} sx={{ textTransform: 'none', fontSize: 12, px: 1.5, py: 0.25 }}>
            Alle
          </ToggleButton>
          {['In progress', 'Pre-productie', 'Post-productie', 'Offerte', 'On Hold', 'Afgerond'].map((s) => (
            <ToggleButton key={s} value={s} sx={{ textTransform: 'none', fontSize: 12, px: 1.5, py: 0.25 }}>
              {s}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Project Grid */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: '#526075' }} />
          </Box>
        ) : projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#adb3b5' }}>
            <Typography fontSize={14}>Geen projecten gevonden</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {projects.map((project) => (
              <Box
                key={project.id}
                onClick={() => handleCardClick(project)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: project.is_pinned ? '2px solid #526075' : '1px solid rgba(45,51,53,0.08)',
                  bgcolor: '#fff',
                  cursor: project.is_pinned ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: '#526075', boxShadow: '0 2px 8px rgba(99,102,241,0.1)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, color: '#767c7e', fontWeight: 600, letterSpacing: 0.5 }}>
                      {project.project_number}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#2d3335', lineHeight: 1.3, mt: 0.25 }} noWrap>
                      {project.project_name_full}
                    </Typography>
                  </Box>
                  <Tooltip title={project.is_pinned ? 'Unpin project' : 'Pin project'}>
                    <IconButton size="small" onClick={(e) => handlePin(e, project)} sx={{ ml: 1, color: project.is_pinned ? '#526075' : '#d1d5db' }}>
                      {project.is_pinned ? <PushPinIcon sx={{ fontSize: 18 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {project.status && (
                    <Chip
                      label={project.status}
                      size="small"
                      sx={{
                        fontSize: 11,
                        height: 22,
                        bgcolor: `${STATUS_COLORS[project.status] || '#767c7e'}15`,
                        color: STATUS_COLORS[project.status] || '#767c7e',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {project.project_type && (
                    <Chip label={project.project_type} size="small" sx={{ fontSize: 11, height: 22, bgcolor: '#f1f4f5', color: '#767c7e' }} />
                  )}
                </Box>
                {project.klant_name && (
                  <Typography sx={{ fontSize: 12, color: '#767c7e', mt: 1 }}>
                    {project.klant_name}
                  </Typography>
                )}
                {project.opdrachtwaarde != null && (
                  <Typography sx={{ fontSize: 12, color: '#adb3b5', mt: 0.5 }}>
                    {'\u20AC'}{Number(project.opdrachtwaarde).toLocaleString('nl-NL', { minimumFractionDigits: 0 })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
